import { useCallback, useEffect, useState } from "react";
import { Badge, Code, Container, Group, Modal, Select, Stack, Table, Text, Title } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { DataTable } from "mantine-datatable";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import HttpClient from "../helpers/HttpClient";

type AuditAction = "read" | "export" | "change" | "login" | "audit_view";

interface AuditRow {
  id: number;
  createdAt: string;
  actorPersonId: number | null;
  actorName: string;
  action: AuditAction;
  area: string;
  method: string | null;
  path: string | null;
  status: number | null;
  ip: string | null;
  userAgent: string | null;
  summary: string;
  details: unknown;
  subjects: { id: number; name: string }[];
}

interface PersonOption { id: number; firstName?: string; lastName?: string }

const ACTIONS: Record<AuditAction, { label: string; color: string }> = {
  read: { label: "Angesehen", color: "blue" },
  export: { label: "Export", color: "orange" },
  change: { label: "Geändert", color: "violet" },
  login: { label: "Anmeldung", color: "gray" },
  audit_view: { label: "Protokoll", color: "dark" },
};

const AREAS: Record<string, string> = {
  person: "Personen", contract: "Verträge", booking: "Buchungen", receipt: "Belege",
  calculation: "Abrechnung", prae: "Prämien", "payment-account": "Zahlungskonten",
  "mobile-account": "Mobile Konten", rental: "Verleih", gdpr: "DSGVO", sepa: "SEPA-Export",
  "training-event": "Termine", "team-draft": "Kader", message: "Nachrichten", invoice: "Rechnungen",
  "consumation-order": "Restaurant", dashboard: "Dashboard", player: "Spieler", event: "Termine",
  "person-list-csv": "Personen-CSV", "invoice-pdf": "Rechnungs-PDF", auth: "Anmeldung", audit: "Protokoll",
  Person: "Person (Änderung)", Contract: "Vertrag (Änderung)", Booking: "Buchung (Änderung)",
  TrainingParticipant: "Teilnahme (Änderung)", Rental: "Ausleihe (Änderung)",
};

const PAGE_SIZE = 50;

// Access and change log (task 029): who saw, exported or changed personal
// data and when. Opening and filtering this page is itself logged.
function AuditLog() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [persons, setPersons] = useState<PersonOption[]>([]);
  const [range, setRange] = useState<[string | null, string | null]>([null, null]);
  const [actor, setActor] = useState<string | null>(null);
  const [subject, setSubject] = useState<string | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [area, setArea] = useState<string | null>(null);
  const [selected, setSelected] = useState<AuditRow | null>(null);

  useEffect(() => {
    new HttpClient<PersonOption>("/audit/persons").getAll().then(setPersons).catch(() => {});
  }, []);

  // Back to page 1 whenever a filter changes.
  useEffect(() => { setPage(1); }, [range, actor, subject, action, area]);

  const load = useCallback(() => {
    // Only complete ranges filter; a half-picked range waits for its end.
    if ((range[0] && !range[1]) || (!range[0] && range[1])) return;
    setLoading(true);
    const params: Record<string, string> = {
      skip: String((page - 1) * PAGE_SIZE),
      take: String(PAGE_SIZE),
    };
    if (range[0]) params.from = dayjs(range[0]).startOf("day").toISOString();
    if (range[1]) params.to = dayjs(range[1]).endOf("day").toISOString();
    if (actor) params.actorPersonId = actor;
    if (subject) params.subjectPersonId = subject;
    if (action) params.action = action;
    if (area) params.area = area;
    new HttpClient<AuditRow>("/audit").getAllPaged(params)
      .then((r) => { setRows(r.data); setTotal(r.total); })
      .catch(() => notifications.show({ color: "red", message: "Protokoll konnte nicht geladen werden." }))
      .finally(() => setLoading(false));
  }, [page, range, actor, subject, action, area]);

  useEffect(() => { load(); }, [load]);

  const personData = persons.map((p) => ({
    value: String(p.id),
    label: `${p.lastName ?? ""} ${p.firstName ?? ""}`.trim() || `#${p.id}`,
  }));

  return (
    <Container fluid mt="md">
      <Title order={2} mb="xs">Protokoll</Title>
      <Text size="sm" c="dimmed" mb="md">
        Wer hat wann personenbezogene Daten angesehen, exportiert oder geändert. Einträge werden nach 12 Monaten gelöscht.
      </Text>

      <Group mb="md" wrap="wrap" align="flex-end">
        <DatePickerInput
          type="range"
          label="Zeitraum"
          placeholder="Alle"
          clearable
          value={range}
          onChange={(v) => setRange(v as [string | null, string | null])}
          valueFormat="DD.MM.YYYY"
          w={240}
        />
        <Select label="Wer" placeholder="Alle" searchable clearable data={personData} value={actor} onChange={setActor} w={220} />
        <Select label="Betroffene Person" placeholder="Alle" searchable clearable data={personData} value={subject} onChange={setSubject} w={220} />
        <Select
          label="Aktion"
          placeholder="Alle"
          clearable
          data={Object.entries(ACTIONS).map(([value, a]) => ({ value, label: a.label }))}
          value={action}
          onChange={setAction}
          w={160}
        />
        <Select
          label="Bereich"
          placeholder="Alle"
          searchable
          clearable
          data={Object.entries(AREAS).map(([value, label]) => ({ value, label }))}
          value={area}
          onChange={setArea}
          w={200}
        />
      </Group>

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        highlightOnHover
        minHeight={200}
        fetching={loading}
        records={rows}
        totalRecords={total}
        recordsPerPage={PAGE_SIZE}
        page={page}
        onPageChange={setPage}
        noRecordsText="Keine Einträge"
        onRowClick={({ record }) => setSelected(record)}
        columns={[
          { accessor: "createdAt", title: "Zeitpunkt", width: 140, render: (r) => dayjs(r.createdAt).format("DD.MM.YY HH:mm:ss") },
          { accessor: "actorName", title: "Wer" },
          {
            accessor: "action",
            title: "Aktion",
            render: (r) => <Badge color={ACTIONS[r.action]?.color ?? "gray"} variant="light">{ACTIONS[r.action]?.label ?? r.action}</Badge>,
          },
          { accessor: "area", title: "Bereich", render: (r) => AREAS[r.area] ?? r.area },
          { accessor: "summary", title: "Was" },
          {
            accessor: "subjects",
            title: "Betroffene",
            render: (r) =>
              r.subjects.length === 0
                ? "–"
                : r.subjects.length <= 3
                  ? r.subjects.map((s) => s.name).join(", ")
                  : `${r.subjects.slice(0, 2).map((s) => s.name).join(", ")} +${r.subjects.length - 2}`,
          },
        ]}
      />

      <Modal opened={!!selected} onClose={() => setSelected(null)} title="Protokolleintrag" size="lg">
        {selected && <AuditDetail row={selected} />}
      </Modal>
    </Container>
  );
}

function AuditDetail({ row }: { row: AuditRow }) {
  const details = (row.details ?? {}) as { diff?: Record<string, { from?: unknown; to?: unknown }> } & Record<string, unknown>;
  const show = (v: unknown) => (v == null || v === "" ? "–" : typeof v === "object" ? JSON.stringify(v) : String(v));
  const { diff, ...rest } = details;
  return (
    <Stack gap="sm">
      <Table withRowBorders={false} verticalSpacing={4}>
        <Table.Tbody>
          <Table.Tr><Table.Td w={140}>Zeitpunkt</Table.Td><Table.Td>{dayjs(row.createdAt).format("DD.MM.YYYY HH:mm:ss")}</Table.Td></Table.Tr>
          <Table.Tr><Table.Td>Wer</Table.Td><Table.Td>{row.actorName}{row.actorPersonId ? ` (#${row.actorPersonId})` : ""}</Table.Td></Table.Tr>
          <Table.Tr><Table.Td>Aktion</Table.Td><Table.Td>{ACTIONS[row.action]?.label ?? row.action} – {row.summary}</Table.Td></Table.Tr>
          <Table.Tr><Table.Td>Anfrage</Table.Td><Table.Td>{row.method ?? ""} {row.path ?? ""} {row.status ? `(${row.status})` : ""}</Table.Td></Table.Tr>
          <Table.Tr><Table.Td>IP-Adresse</Table.Td><Table.Td>{row.ip ?? "–"}</Table.Td></Table.Tr>
          <Table.Tr><Table.Td>Gerät</Table.Td><Table.Td><Text size="xs" c="dimmed">{row.userAgent ?? "–"}</Text></Table.Td></Table.Tr>
        </Table.Tbody>
      </Table>

      {diff && Object.keys(diff).length > 0 && (
        <>
          <Text fw={600} size="sm">Änderungen</Text>
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr><Table.Th>Feld</Table.Th><Table.Th>Vorher</Table.Th><Table.Th>Nachher</Table.Th></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {Object.entries(diff).map(([field, d]) => {
                // Relation lists (tags, parents, …) come as ids: added / removed,
                // or only the new state when the previous one is unknown.
                const rel = d as { added?: string[]; removed?: string[]; set?: string[] };
                const ids = (list?: string[]) => (list?.length ? list.map((id) => `#${id}`).join(", ") : "–");
                const isRelation = !!(rel.added || rel.removed || rel.set);
                return (
                  <Table.Tr key={field}>
                    <Table.Td>{field}</Table.Td>
                    <Table.Td>{isRelation ? (rel.removed ? `entfernt: ${ids(rel.removed)}` : "–") : "from" in (d ?? {}) ? show(d.from) : "–"}</Table.Td>
                    <Table.Td>
                      {isRelation
                        ? rel.set ? `jetzt: ${ids(rel.set)}` : `hinzugefügt: ${ids(rel.added)}`
                        : "to" in (d ?? {}) ? show(d.to) : "–"}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </>
      )}

      {row.subjects.length > 0 && (
        <>
          <Text fw={600} size="sm">Betroffene Personen ({row.subjects.length})</Text>
          <Text size="sm">{row.subjects.map((s) => s.name).join(", ")}</Text>
        </>
      )}

      {Object.keys(rest).length > 0 && (
        <>
          <Text fw={600} size="sm">Details</Text>
          <Code block>{JSON.stringify(rest, null, 2)}</Code>
        </>
      )}
    </Stack>
  );
}

export default AuditLog;
